import {
  fiscalJobItemReason,
  FiscalJobItem,
  FiscalJobItemState,
  FiscalJobState
} from '../fiscal-jobs/fiscal-job.models';
import {ExportTone, formatDay, UNNAMED_REGISTER_LABEL} from '../exports/data-export.models';

/**
 * Correcting filed cash point closings (#771): the wire types this screen adds on top of the fiscal job engine,
 * and the German words it puts on them.
 *
 * A correction withdraws a closing that was already filed and files it again, rebuilt from the stored data,
 * under a new reference. It exists to repair closings hit by Bugs #750/#751. It is a fiscal job of kind
 * `closingCorrection`, so its runs, entries and retries are the engine's (`/fiscal/jobs/*`); only picking the
 * closings and starting the run are specific to it.
 *
 * ### Nothing here names a provider
 *
 * Same rule as every screen of Feature #241. A closing is described by its register, its business day and its
 * number, never by who receives it.
 *
 * ### Why the German wording lives here and not in the templates
 *
 * The run list, an entry line and the alert after a refused start all render the same codes and states. One
 * table per concern keeps them reading the same way and lets one spec assert them.
 */

/** The longest reason the server accepts, in characters. Mirrors the create contract. */
export const CLOSING_CORRECTION_REASON_MAX = 500;

/** The most closings one run may cover. Mirrors the server's `E_FISCAL_CORRECTION_TOO_MANY` bound. */
export const CLOSING_CORRECTION_MAX_ITEMS = 500;

/**
 * Where a closing stands locally, as the candidate list reports it.
 *
 * Both values are eligible. `transmitted` means the filing was accepted for processing, `completed` that it
 * was confirmed as processed.
 */
export type ClosingLocalState = 'transmitted' | 'completed';

/** One filed closing that may be corrected. */
export interface ClosingCorrectionCandidate {
  /** The closing's id, which is what a create request names. Never rendered. */
  entryId: string;
  /** The register it belongs to. Never rendered. */
  registerId: string;
  /**
   * The operator's name for the register, or `null` when nobody named it.
   *
   * Normalised to `null` by the HTTP service: the monolith drops null properties from its JSON, so an unnamed
   * register would otherwise arrive as `undefined` and slip past every `=== null` check.
   */
  registerName: string | null;
  /** The closing's running number on its register. */
  closingNumber: number;
  /** The business day it closed, `yyyy-MM-dd`. */
  businessDate: string;
  localState: ClosingLocalState;
  /**
   * Whether a correction of this closing is already running.
   *
   * Such a closing cannot be picked again: two runs would each withdraw and refile it, and the second one
   * would withdraw what the first one just filed.
   */
  correctionOutstanding: boolean;
  /** How often this closing has been filed so far. 1 means it was never corrected. */
  filings: number;
}

/** The filter the candidate list is read with. */
export interface ClosingCorrectionCandidateQuery {
  /** The first business day, `yyyy-MM-dd`. */
  from: string;
  /** The last business day, inclusive, `yyyy-MM-dd`. */
  to: string;
  /** The registers to cover, or empty for every register. */
  registerIds: string[];
}

/** What starting a correction asks for. */
export interface ClosingCorrectionCreateRequest {
  /** The closings to withdraw and file again. */
  entryIds: string[];
  /** Why they are corrected. Required, at most {@link CLOSING_CORRECTION_REASON_MAX} characters. */
  reason: string;
}

/** What to call a candidate's register on screen: its own name, never its id. */
export function candidateRegisterLabel(candidate: Pick<ClosingCorrectionCandidate, 'registerName'>): string {
  return candidate.registerName ?? UNNAMED_REGISTER_LABEL;
}

/**
 * The reason a run was started with, read back off its stored parameters (`{"reason": "…"}`), or `null`.
 *
 * Tolerant on purpose, like `parseExportPeriod`: a run whose parameters a later build cannot read must still
 * render as a row, just without its reason.
 */
export function parseCorrectionReason(parameters: string | null | undefined): string | null {
  if (!parameters) return null;
  try {
    const parsed: unknown = JSON.parse(parameters);
    if (!parsed || typeof parsed !== 'object') return null;
    const reason = (parsed as Record<string, unknown>)['reason'];
    return typeof reason === 'string' && reason.trim().length > 0 ? reason.trim() : null;
  } catch {
    return null;
  }
}

/** One candidate as a single line: `03.09.2026 · Theke`. */
export function candidateTitle(candidate: ClosingCorrectionCandidate): string {
  return `${formatDay(candidate.businessDate)} · ${candidateRegisterLabel(candidate)}`;
}

/**
 * Puts candidates in the order the list shows them: by business day, then register, then closing number.
 *
 * Sorted here rather than trusted from the server, so a reload never reshuffles the rows under a reader who is
 * halfway through ticking them.
 */
export function sortCandidates(candidates: readonly ClosingCorrectionCandidate[]): ClosingCorrectionCandidate[] {
  return [...candidates].sort((a, b) =>
    a.businessDate.localeCompare(b.businessDate)
    || candidateRegisterLabel(a).localeCompare(candidateRegisterLabel(b), 'de')
    || a.closingNumber - b.closingNumber);
}

/** „1 Kassenabschluss", „3 Kassenabschlüsse". */
export function closingCountLabel(count: number): string {
  return count === 1 ? '1 Kassenabschluss' : `${count} Kassenabschlüsse`;
}

/** The German label for a candidate's local state. */
export function localStateLabel(state: ClosingLocalState): string {
  return state === 'completed' ? 'Angenommen' : 'Übermittelt';
}

/** The German label for a correction run's state. */
export function correctionRunStateLabel(state: FiscalJobState): string {
  return RUN_STATE_LABELS[state] ?? 'Unbekannt';
}

/** The German label for one entry's state. */
export function correctionItemStateLabel(state: FiscalJobItemState): string {
  return ITEM_STATE_LABELS[state] ?? 'Unbekannt';
}

/**
 * The tone an entry's chip is drawn in. Same split as the export screens: `expired` and `withdrawn` ask for a
 * new attempt, `failed` and `cancelled` report a fault.
 */
export function correctionItemTone(state: FiscalJobItemState): ExportTone {
  switch (state) {
    case 'completed':
      return 'success';
    case 'pending':
    case 'working':
      return 'running';
    case 'expired':
    case 'withdrawn':
      return 'warning';
    default:
      return 'danger';
  }
}

/** The German sentence for a code, falling back to a generic one. Never the code, never the server's English. */
export function correctionErrorMessage(code: string | null | undefined): string {
  return correctionErrorSentence(code) ?? FALLBACK_ERROR;
}

/** The German sentence for a code, or `null` when this screen has none for it. */
export function correctionErrorSentence(code: string | null | undefined): string | null {
  if (!code) return null;
  return ERROR_MESSAGES[code] ?? null;
}

/**
 * Why one entry of a correction did not come out, in German. The precedence (stored detail, then our sentence,
 * then a generic one) is the engine's, see {@link fiscalJobItemReason}.
 */
export function correctionItemReason(item: Pick<FiscalJobItem, 'errorCode' | 'errorDetail'>): string {
  return fiscalJobItemReason(item, correctionErrorSentence, FALLBACK_ERROR);
}

const RUN_STATE_LABELS: Record<FiscalJobState, string> = {
  queued: 'In der Warteschlange',
  running: 'Läuft',
  completed: 'Abgeschlossen',
  completedWithErrors: 'Teilweise abgeschlossen',
  failed: 'Fehlgeschlagen'
};

const ITEM_STATE_LABELS: Record<FiscalJobItemState, string> = {
  pending: 'Wartet',
  working: 'Wird übermittelt',
  completed: 'Neu übermittelt',
  failed: 'Fehlgeschlagen',
  cancelled: 'Abgebrochen',
  expired: 'Abgelaufen',
  withdrawn: 'Nicht mehr verfügbar'
};

const FALLBACK_ERROR =
  'Der Vorgang konnte nicht abgeschlossen werden. Bitte versuchen Sie es erneut oder wenden Sie sich an die ' +
  'Administration.';

/**
 * Every refusal this screen can meet, in German and in the formal Sie. Each sentence says what happened and
 * what to do next.
 *
 * The engine's own codes are worded here again rather than borrowed from the export screen, because those
 * sentences talk about files and exports and a correction produces neither.
 */
const ERROR_MESSAGES: Record<string, string> = {
  // ----- starting a correction -----
  E_FISCAL_CORRECTION_NONE_SELECTED: 'Bitte wählen Sie mindestens einen Kassenabschluss aus.',
  E_FISCAL_CORRECTION_NOT_ELIGIBLE:
    'Mindestens einer der gewählten Kassenabschlüsse kann nicht erneut übermittelt werden. Bitte laden Sie ' +
    'die Liste neu und wählen Sie erneut.',
  E_FISCAL_CORRECTION_OUTSTANDING:
    'Für mindestens einen der gewählten Kassenabschlüsse läuft bereits eine erneute Übermittlung. Bitte ' +
    'warten Sie, bis sie abgeschlossen ist, und laden Sie die Liste dann neu.',
  E_FISCAL_CORRECTION_TOO_MANY:
    `Es können höchstens ${CLOSING_CORRECTION_MAX_ITEMS} Kassenabschlüsse auf einmal erneut übermittelt ` +
    'werden. Bitte grenzen Sie die Auswahl ein.',
  // The screen checks both before it sends anything; the server has the final word.
  E_FISCAL_CORRECTION_REASON_REQUIRED:
    `Bitte geben Sie einen Grund für die erneute Übermittlung an. Er darf höchstens ` +
    `${CLOSING_CORRECTION_REASON_MAX} Zeichen lang sein.`,
  E_FISCAL_CORRECTION_PERIOD_INVALID:
    'Der gewählte Zeitraum ist nicht gültig. Das Bis-Datum darf nicht vor dem Von-Datum liegen.',
  E_FISCAL_CORRECTION_RECORDS_UNAVAILABLE:
    'Die Kassenabschlüsse konnten gerade nicht gelesen werden. Es wurde nichts verändert. Bitte versuchen Sie ' +
    'es in Kürze erneut.',
  // A location with no fiscal provider is refused the same way the data export refuses it (#770). The
  // export's code is mapped too, in case the refusal comes from the shared check.
  E_FISCAL_CORRECTION_NOT_CONFIGURED:
    'Für diesen Standort ist keine Kassenführung eingerichtet. Es können keine Kassenabschlüsse erneut ' +
    'übermittelt werden.',
  E_FISCAL_EXPORT_NOT_CONFIGURED:
    'Für diesen Standort ist keine Kassenführung eingerichtet. Es können keine Kassenabschlüsse erneut ' +
    'übermittelt werden.',
  // Die beiden Sätze darüber behaupten etwas ÜBER den Standort; dieser spricht nur über den Lesevorgang
  // (#829). Bis dahin teilten sie sich einen Code, sodass eine kurze Störung einem Standort, der täglich
  // abschließt, bescheinigte, er führe keine Kasse.
  E_FISCAL_CORRECTION_SETTINGS_UNAVAILABLE:
    'Die Einstellungen zur Kassenführung konnten gerade nicht gelesen werden. Es wurde nichts geändert. ' +
    'Bitte versuchen Sie es in Kürze erneut.',
  E_FISCAL_EXPORT_SETTINGS_UNAVAILABLE:
    'Die Einstellungen zur Kassenführung konnten gerade nicht gelesen werden. Es wurde nichts geändert. ' +
    'Bitte versuchen Sie es in Kürze erneut.',

  // ----- one closing of a run -----
  // The check runs before anything is withdrawn, so the filed closing is untouched. Saying so matters: the
  // reader's first worry is whether the day is now missing.
  E_FISCAL_CORRECTION_VAT_DRIFT:
    'Ein Steuersatz in diesem Kassenabschluss wurde seit dem Abschluss geändert. Der Abschluss wurde deshalb ' +
    'nicht zurückgezogen und bleibt unverändert bestehen. Bitte wenden Sie sich an die Administration.',
  E_FISCAL_CORRECTION_CLIENT_UNKNOWN:
    'Die Kasse, unter der dieser Kassenabschluss übermittelt wurde, ließ sich nicht feststellen. Der Abschluss ' +
    'wurde deshalb nicht zurückgezogen und bleibt unverändert bestehen. Bitte wenden Sie sich an die ' +
    'Administration.',
  // The two below happen AFTER the withdrawal, so the day is missing from the export until this is solved.
  // That is the one thing the reader must take away, so it is said plainly.
  E_FISCAL_CORRECTION_CONFLICT:
    'Der neu übermittelte Kassenabschluss wurde wegen eines Konflikts nicht angenommen. Der bisherige ' +
    'Abschluss ist bereits zurückgezogen, daher fehlt dieser Tag im Datenexport, bis das geklärt ist. Bitte ' +
    'wenden Sie sich an die Administration, bevor Sie es erneut versuchen.',
  E_FISCAL_CORRECTION_REJECTED:
    'Der neu übermittelte Kassenabschluss wurde nach dem Senden abgelehnt. Der bisherige Abschluss ist ' +
    'bereits zurückgezogen, daher fehlt dieser Tag im Datenexport, bis das geklärt ist. Bitte wenden Sie sich ' +
    'an die Administration.',
  // Answered on the START as well as on an entry (Bug #861): a data export that could still be building over
  // the period of a selected closing refuses the run before it exists. That includes an export whose entry
  // already failed while the record store had not yet confirmed what became of it. Nothing was withdrawn.
  E_FISCAL_CORRECTION_EXPORT_OUTSTANDING:
    'Für den Zeitraum mindestens eines der gewählten Kassenabschlüsse wird gerade ein Datenexport erstellt ' +
    'oder wartet noch auf die Bestätigung des Ablageorts. Solange kann kein Kassenabschluss dieses Zeitraums ' +
    'erneut übermittelt werden. Es wurde nichts zurückgezogen. Bitte warten Sie, bis der Export unter ' +
    '„DSFinV-K-Datenexport“ abgeschlossen ist, und versuchen Sie es dann erneut.',

  // ----- the engine -----
  E_FISCAL_JOB_TENANT_REQUIRED: 'Bitte wählen Sie zuerst einen Standort aus.',
  E_SESSION_SHARD_REQUIRED: 'Bitte wählen Sie zuerst einen Standort aus.',
  E_FISCAL_JOB_NO_ITEMS: 'Bitte wählen Sie mindestens einen Kassenabschluss aus.',
  E_FISCAL_JOB_TOO_MANY_ITEMS:
    'Es wurden mehr Kassenabschlüsse ausgewählt, als ein Lauf umfassen darf. Bitte grenzen Sie die Auswahl ein.',
  E_FISCAL_JOB_NOT_FOUND: 'Die Übermittlung wurde nicht gefunden. Bitte laden Sie die Seite neu.',
  E_FISCAL_JOB_ITEM_NOT_FOUND: 'Der Eintrag wurde nicht gefunden. Bitte laden Sie die Seite neu.',
  E_FISCAL_JOB_ITEM_NOT_RETRYABLE:
    'Dieser Eintrag wird gerade noch verarbeitet. Bitte warten Sie, bis er abgeschlossen ist.',
  E_FISCAL_JOB_ITEM_ALREADY_COMPLETED:
    'Dieser Kassenabschluss wurde bereits neu übermittelt. Ein weiterer Versuch ist nicht nötig.',
  E_FISCAL_JOB_NO_HANDLER:
    'Die erneute Übermittlung von Kassenabschlüssen steht auf dieser Installation nicht zur Verfügung. Bitte ' +
    'wenden Sie sich an die Administration.',
  E_FISCAL_JOB_OPERATION_FAILED:
    'Dieser Kassenabschluss konnte nicht neu übermittelt werden. Sie können ihn einzeln erneut versuchen.',
  E_FISCAL_JOB_OPERATION_CANCELLED: 'Die Übermittlung dieses Kassenabschlusses wurde vorzeitig beendet.',
  E_FISCAL_JOB_OPERATION_EXPIRED:
    'Dieser Kassenabschluss wurde nicht rechtzeitig bestätigt. Bitte versuchen Sie ihn erneut.',
  E_FISCAL_JOB_OPERATION_WITHDRAWN:
    'Die neue Übermittlung dieses Kassenabschlusses liegt nicht mehr vor. Bitte versuchen Sie ihn erneut.',
  E_FISCAL_JOB_POLL_BUDGET_EXHAUSTED:
    'Die Bestätigung dieses Kassenabschlusses hat länger gedauert als erwartet und wird nicht mehr abgewartet. ' +
    'Sie können ihn einzeln erneut versuchen.',
  E_FISCAL_JOB_START_INTERRUPTED:
    'Die Übermittlung dieses Kassenabschlusses wurde unterbrochen. Sie können ihn einzeln erneut versuchen.',
  E_FISCAL_JOB_INFRASTRUCTURE_UNAVAILABLE:
    'Ein benötigtes System war nicht erreichbar. Sie können diesen Kassenabschluss erneut versuchen.'
};
